/**
 * Unified Anant Admin Console — covers:
 *  • Smart Card limits (global + per-user override)
 *  • Freedom Academy: custom category CRUD + course/module CRUD
 *  • Live Trading Phase 3 go-live toggle
 * One page, one place. Minimalist UI, full power.
 */
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Settings, Plus, Trash2, Edit3, Save, X, GraduationCap, Sparkles,
  Activity, Rocket, ShieldAlert, Wallet, Check, Clock, ExternalLink, Tag, Heart,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import api from '../../utils/api';

const AnantAdminConsole = () => {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'phase3';
  const [tab, setTab] = useState(initialTab);
  return (
    <div className="min-h-screen pt-24 pb-12 px-4 bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-950 text-white" data-testid="anant-admin-console">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <div className="text-amber-400 text-xs font-bold uppercase tracking-widest flex items-center gap-1.5"><Settings className="w-4 h-4" /> Admin Console</div>
          <h1 className="text-3xl font-extrabold mt-1">Anant Web Portal — Master Controls</h1>
        </div>
        <div className="flex flex-wrap gap-2 mb-5">
          {[
            { id: 'atdeposit', label: 'AT Deposits (Buy AT)', icon: Wallet },
            { id: 'sponsored', label: 'Sponsored Links',      icon: Tag },
            { id: 'feelings',  label: 'Feelings Personas',    icon: Heart },
            { id: 'phase3',    label: 'Live Trading Phase 3', icon: Rocket },
            { id: 'smartcard', label: 'Smart Card Limits',    icon: Sparkles },
            { id: 'categories',label: 'Academy Categories',   icon: GraduationCap },
            { id: 'courses',   label: 'Academy Courses',      icon: GraduationCap },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`px-3 py-2 rounded-lg text-xs font-bold flex items-center gap-1.5 border ${tab === t.id ? 'bg-amber-500/20 border-amber-500/50 text-amber-200' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === 'atdeposit'  && <ATDepositTab />}
        {tab === 'sponsored'  && <SponsoredLinksTab />}
        {tab === 'feelings'   && <FeelingsPersonasTab />}
        {tab === 'phase3'     && <Phase3Tab />}
        {tab === 'smartcard'  && <SmartCardTab />}
        {tab === 'categories' && <CategoriesTab />}
        {tab === 'courses'    && <CoursesTab />}
      </div>
    </div>
  );
};

// ============ AT Deposits (Buy AT) ============
const ATDepositTab = () => {
  const [settings, setSettings] = useState(null);
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [savingS, setSavingS] = useState(false);
  const [edit, setEdit] = useState({});
  const [zoomImg, setZoomImg] = useState(null);
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvResult, setCsvResult] = useState(null);

  const uploadCsv = async (file) => {
    if (!file) return;
    setCsvBusy(true); setCsvResult(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/at-deposit/admin/csv-match', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCsvResult(r.data);
      toast.success(`✓ Matched ${r.data.counts.matched} / ${r.data.counts.pending_total} pending`);
    } catch (e) { toast.error(e.response?.data?.detail || 'CSV parse failed'); }
    finally { setCsvBusy(false); }
  };

  const bulkApproveMatched = async () => {
    if (!csvResult?.matched?.length) return;
    if (!window.confirm(`Approve ${csvResult.matched.length} matched deposits?`)) return;
    let ok = 0, fail = 0;
    for (const m of csvResult.matched) {
      try { await api.post(`/at-deposit/admin/decide/${m.deposit.id}`, { decision: 'approve', admin_notes: `Auto-matched via CSV UTR ${m.utr}` }); ok++; }
      catch { fail++; }
    }
    toast.success(`✓ ${ok} approved${fail ? ` · ${fail} failed` : ''}`);
    setCsvResult(null); loadAll();
  };

  const loadAll = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get('/at-deposit/admin/settings'),
        api.get('/at-deposit/admin/requests', { params: filter === 'all' ? {} : { status: filter } }),
      ]);
      setSettings(a.data.settings);
      setEdit(a.data.settings);
      setRequests(b.data.deposits || []);
    } catch (e) { toast.error('Admin only'); }
  };
  useEffect(() => { loadAll(); }, [filter]);

  const saveSettings = async () => {
    setSavingS(true);
    try {
      const payload = { ...edit };
      // Convert numeric fields
      ['min_amount', 'max_amount', 'rate'].forEach(k => { if (payload[k] != null) payload[k] = Number(payload[k]); });
      await api.post('/at-deposit/admin/settings', payload);
      toast.success('Settings saved');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSavingS(false); }
  };

  const decide = async (id, decision) => {
    const notes = window.prompt(`${decision === 'approve' ? 'Approve' : 'Reject'} — admin notes (optional):`) || '';
    if (decision === 'reject' && !window.confirm('Reject this deposit request?')) return;
    try {
      const r = await api.post(`/at-deposit/admin/decide/${id}`, { decision, admin_notes: notes });
      toast.success(`Request ${decision}d`);
      // Auto-open WA notification tab if user has a WA number
      if (r.data?.wa_link) {
        setTimeout(() => window.open(r.data.wa_link, '_blank', 'noopener'), 400);
      }
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (!settings) return <Loading />;

  return (
    <div className="space-y-5" data-testid="admin-atdeposit-panel">
      {/* Settings card */}
      <div className="rounded-2xl border border-amber-500/30 bg-slate-950/60 p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-amber-300 font-bold text-lg flex items-center gap-2"><Wallet className="w-5 h-5" /> Payment Methods (UPI / QR / Bank)</div>
            <div className="text-xs text-slate-400">Rate: 1 INR = {settings.rate || 1} AT · {settings.is_active ? 'ACTIVE' : 'PAUSED'}</div>
          </div>
          <button onClick={saveSettings} disabled={savingS} data-testid="at-save-settings" className="px-3 py-2 rounded-lg bg-amber-500 text-black text-xs font-bold inline-flex items-center gap-1.5">
            <Save className="w-3.5 h-3.5" /> {savingS ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            ['UPI ID', 'upi_id'],
            ['UPI Payee Name', 'upi_payee_name'],
            ['Bank Account Holder', 'bank_account_name'],
            ['Bank Account Number', 'bank_account_number'],
            ['IFSC', 'bank_ifsc'],
            ['Bank Name', 'bank_name'],
            ['QR Image URL', 'qr_image_url'],
            ['Min Amount (₹)', 'min_amount'],
            ['Max Amount (₹)', 'max_amount'],
            ['Rate (AT per ₹)', 'rate'],
          ].map(([label, key]) => (
            <label key={key} className="block text-xs">
              <div className="text-slate-400 mb-1">{label}</div>
              <input
                data-testid={`at-setting-${key}`}
                value={edit[key] ?? ''}
                onChange={e => setEdit({ ...edit, [key]: e.target.value })}
                className="w-full px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-700 text-slate-100"
              />
            </label>
          ))}
          <label className="block text-xs sm:col-span-2">
            <div className="text-slate-400 mb-1">Instructions to users</div>
            <textarea
              data-testid="at-setting-instructions"
              value={edit.instructions ?? ''}
              onChange={e => setEdit({ ...edit, instructions: e.target.value })}
              rows={3}
              className="w-full px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-700 text-slate-100"
            />
          </label>
          <label className="flex items-center gap-2 text-xs sm:col-span-2">
            <input
              type="checkbox"
              checked={!!edit.is_active}
              onChange={e => setEdit({ ...edit, is_active: e.target.checked })}
              data-testid="at-setting-active"
            />
            <span className="text-slate-200">Deposits ACTIVE (uncheck to pause new requests)</span>
          </label>
        </div>
      </div>

      {/* Requests list */}
      <div className="rounded-2xl border border-white/10 bg-slate-950/60 p-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div className="font-bold text-slate-200">Deposit Requests</div>
          <div className="flex gap-2">
            {['pending', 'approved', 'rejected', 'all'].map(f => (
              <button
                key={f}
                data-testid={`at-filter-${f}`}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${filter === f ? 'bg-amber-500 text-black border-amber-400' : 'bg-slate-800 text-slate-300 border-slate-700'}`}
              >
                {f}
              </button>
            ))}
            <button onClick={loadAll} className="px-3 py-1.5 rounded-full text-xs bg-slate-800 text-slate-200">↻ Refresh</button>
          </div>
        </div>

        {/* 📄 Bank Statement CSV Auto-Match */}
        <div className="rounded-xl border-2 border-emerald-400/40 bg-emerald-500/5 p-3 mb-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">📄 Auto-Match via Bank Statement CSV</div>
              <div className="text-[11px] opacity-80">Bank statement CSV upload karo — UTR/Reference column detect hoga → pending deposits ke saath match hoga → ek click me bulk approve.</div>
            </div>
            <label className="text-[11px] font-black inline-flex items-center gap-1 cursor-pointer">
              <input type="file" accept=".csv,text/csv" onChange={(e) => uploadCsv(e.target.files?.[0])} data-testid="atdep-csv-upload" className="hidden" />
              <span className="px-3 py-1.5 rounded bg-emerald-500 text-black">{csvBusy ? '⏳ Parsing…' : '📤 Upload CSV'}</span>
            </label>
          </div>
          {csvResult && (
            <div className="mt-3 text-[11px]">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <span className="font-black text-emerald-300">✓ {csvResult.counts.matched} matched</span>
                <span className="opacity-70">· {csvResult.counts.unmatched} unmatched · {csvResult.counts.pending_total} pending total</span>
                {csvResult.matched.length > 0 && (
                  <button onClick={bulkApproveMatched} data-testid="atdep-csv-approve-all" className="px-3 py-1 rounded bg-gradient-to-r from-emerald-500 to-cyan-500 text-black font-black">✓ Approve All Matched</button>
                )}
              </div>
              {csvResult.matched.length > 0 && (
                <div className="max-h-40 overflow-y-auto rounded bg-slate-950/60 border border-slate-800 p-2 space-y-0.5">
                  {csvResult.matched.map((m, i) => (
                    <div key={i} className="text-[10px] flex items-center gap-2">
                      <span className="text-emerald-300">✓</span>
                      <span className="opacity-70">UTR</span> <span className="font-mono">{m.utr}</span>
                      <span className="opacity-70">→</span>
                      <span className="font-bold">₹{m.deposit.amount_inr}</span>
                      <span className="opacity-70">· @{m.deposit.user_name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        {requests.length === 0 ? (
          <div className="text-center py-10 text-slate-500 text-sm">No requests</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-slate-400">
                <tr className="text-left">
                  <th className="py-2 pr-3">User</th>
                  <th className="py-2 pr-3">Amount</th>
                  <th className="py-2 pr-3">Method</th>
                  <th className="py-2 pr-3">UTR / Ref</th>
                  <th className="py-2 pr-3">Proof</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Actions</th>
                </tr>
              </thead>
              <tbody className="text-slate-200">
                {requests.map(r => (
                  <tr key={r.id} className="border-t border-slate-800" data-testid={`at-row-${r.id}`}>
                    <td className="py-2 pr-3">
                      <div className="font-semibold">{r.user_name || '—'}</div>
                      <div className="text-slate-500 text-[10px]">{r.user_email}</div>
                    </td>
                    <td className="py-2 pr-3">
                      <div className="font-bold text-amber-300">₹{r.amount_inr}</div>
                      <div className="text-[10px] text-emerald-300">→ {r.at_equivalent} AT</div>
                    </td>
                    <td className="py-2 pr-3 uppercase">{r.method}</td>
                    <td className="py-2 pr-3 font-mono">{r.txn_ref}</td>
                    <td className="py-2 pr-3">
                      {r.screenshot_url ? (
                        <button
                          type="button"
                          onClick={() => setZoomImg(r.screenshot_url.startsWith('http') ? r.screenshot_url : `${process.env.REACT_APP_BACKEND_URL}${r.screenshot_url}`)}
                          title="Click to zoom"
                          data-testid={`at-proof-${r.id}`}
                          className="inline-block cursor-zoom-in"
                        >
                          <img
                            src={r.screenshot_url.startsWith('http') ? r.screenshot_url : `${process.env.REACT_APP_BACKEND_URL}${r.screenshot_url}`}
                            alt="Payment proof"
                            className="w-14 h-14 rounded-md border-2 border-amber-400/50 object-cover hover:scale-110 transition"
                          />
                        </button>
                      ) : <span className="text-slate-500 text-[10px]">no proof</span>}
                    </td>
                    <td className="py-2 pr-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${r.status === 'approved' ? 'bg-emerald-500/20 text-emerald-300' : r.status === 'rejected' ? 'bg-rose-500/20 text-rose-300' : 'bg-amber-500/20 text-amber-300'}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="py-2 pr-3">
                      {r.status === 'pending' ? (
                        <div className="flex gap-1">
                          <button data-testid={`at-approve-${r.id}`} onClick={() => decide(r.id, 'approve')} className="px-2 py-1 rounded-md bg-emerald-500 text-black text-[10px] font-bold inline-flex items-center gap-1"><Check className="w-3 h-3" /> Approve</button>
                          <button data-testid={`at-reject-${r.id}`} onClick={() => decide(r.id, 'reject')} className="px-2 py-1 rounded-md bg-rose-500 text-white text-[10px] font-bold inline-flex items-center gap-1"><X className="w-3 h-3" /> Reject</button>
                        </div>
                      ) : (
                        <div className="text-[10px] text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3" /> {(r.decided_at || '').slice(0, 16).replace('T', ' ')}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 🔍 Screenshot Lightbox Modal */}
      {zoomImg && <ScreenshotLightbox url={zoomImg} onClose={() => setZoomImg(null)} />}
    </div>
  );
};

// Zoomable lightbox — scroll to zoom, drag to pan, ESC / click backdrop to close.
const ScreenshotLightbox = ({ url, onClose }) => {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const zoomIn = () => setScale((s) => Math.min(6, s * 1.2));
  const zoomOut = () => setScale((s) => Math.max(0.5, s / 1.2));
  const reset = () => { setScale(1); setOffset({ x: 0, y: 0 }); };
  const onWheel = (e) => { e.preventDefault(); setScale((s) => Math.max(0.5, Math.min(6, s * (e.deltaY < 0 ? 1.1 : 0.9)))); };
  const onDown = (e) => { dragRef.current = { x: e.clientX - offset.x, y: e.clientY - offset.y }; };
  const onMove = (e) => { if (dragRef.current) setOffset({ x: e.clientX - dragRef.current.x, y: e.clientY - dragRef.current.y }); };
  const onUp = () => { dragRef.current = null; };
  return (
    <div
      data-testid="atdep-lightbox"
      className="fixed inset-0 z-[999] bg-black/85 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute top-3 right-3 flex gap-2 z-10" onClick={(e) => e.stopPropagation()}>
        <button onClick={zoomOut} data-testid="lightbox-zoom-out" className="px-3 py-1.5 rounded bg-slate-800 text-white text-xs font-black">−</button>
        <button onClick={reset} data-testid="lightbox-reset" className="px-3 py-1.5 rounded bg-slate-800 text-white text-xs font-black">{Math.round(scale * 100)}%</button>
        <button onClick={zoomIn} data-testid="lightbox-zoom-in" className="px-3 py-1.5 rounded bg-slate-800 text-white text-xs font-black">+</button>
        <a href={url} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded bg-slate-800 text-white text-xs font-black">↗ New tab</a>
        <button onClick={onClose} data-testid="lightbox-close" className="px-3 py-1.5 rounded bg-red-500 text-white text-xs font-black">✕ Close</button>
      </div>
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-[10px] text-white/60 select-none">Scroll to zoom · Drag to pan · ESC to close</div>
      <div
        className="w-full h-full overflow-hidden flex items-center justify-center cursor-grab active:cursor-grabbing"
        onClick={(e) => e.stopPropagation()}
        onWheel={onWheel}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerLeave={onUp}
      >
        <img
          src={url}
          alt="Payment screenshot"
          className="max-w-none select-none pointer-events-none"
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
            transition: dragRef.current ? 'none' : 'transform 120ms ease-out',
            maxHeight: '90vh',
          }}
          draggable={false}
        />
      </div>
    </div>
  );
};

// ============ Sponsored Links ============
const SponsoredLinksTab = () => {
  const [links, setLinks] = useState([]);
  const [edit, setEdit] = useState(null);
  const empty = { title: '', subtitle: '', url: '', image_url: '', cta_text: 'Shop Now', category: '', sort_order: 100, is_active: true };
  const load = () => api.get('/sponsored-links/admin/list').then(r => setLinks(r.data.links || [])).catch(() => toast.error('Admin only'));
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!edit.title || !edit.url) { toast.error('Title + URL required'); return; }
    try {
      if (edit.id) await api.post(`/sponsored-links/admin/update/${edit.id}`, edit);
      else await api.post('/sponsored-links/admin/create', edit);
      toast.success('Saved'); setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this link?')) return;
    try { await api.delete(`/sponsored-links/admin/delete/${id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Delete failed'); }
  };
  return (
    <div className="space-y-4" data-testid="admin-sponsored-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-bold text-slate-100 text-lg">Sponsored Tools Widget (Tarot Card pages)</div>
          <div className="text-xs text-slate-400">Plug-and-play affiliate links — Rudraksha, Gemstones, Astro consults, courses. Each click is tracked.</div>
        </div>
        <button data-testid="sponsored-add-btn" onClick={() => setEdit({ ...empty })} className="px-3 py-2 rounded-lg bg-amber-500 text-black text-xs font-bold inline-flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Add Link</button>
      </div>

      {edit && (
        <div className="rounded-2xl border border-amber-500/30 bg-slate-950/80 p-4 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            <label><div className="text-slate-400 mb-1">Title *</div><input data-testid="sp-title" value={edit.title} onChange={e => setEdit({ ...edit, title: e.target.value })} className="w-full px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-700 text-slate-100" /></label>
            <label><div className="text-slate-400 mb-1">Subtitle</div><input value={edit.subtitle || ''} onChange={e => setEdit({ ...edit, subtitle: e.target.value })} className="w-full px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-700 text-slate-100" /></label>
            <label className="sm:col-span-2"><div className="text-slate-400 mb-1">Affiliate URL *</div><input data-testid="sp-url" value={edit.url} onChange={e => setEdit({ ...edit, url: e.target.value })} placeholder="https://amzn.to/xyz" className="w-full px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-700 text-slate-100" /></label>
            <label className="sm:col-span-2"><div className="text-slate-400 mb-1">Image URL</div><input value={edit.image_url || ''} onChange={e => setEdit({ ...edit, image_url: e.target.value })} placeholder="https://..." className="w-full px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-700 text-slate-100" /></label>
            <label><div className="text-slate-400 mb-1">CTA Text</div><input value={edit.cta_text || ''} onChange={e => setEdit({ ...edit, cta_text: e.target.value })} className="w-full px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-700 text-slate-100" /></label>
            <label><div className="text-slate-400 mb-1">Category</div><input value={edit.category || ''} onChange={e => setEdit({ ...edit, category: e.target.value })} placeholder="Rudraksha / Gemstone / Course" className="w-full px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-700 text-slate-100" /></label>
            <label><div className="text-slate-400 mb-1">Sort order</div><input type="number" value={edit.sort_order} onChange={e => setEdit({ ...edit, sort_order: Number(e.target.value) })} className="w-full px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-700 text-slate-100" /></label>
            <label className="flex items-center gap-2 mt-3"><input type="checkbox" checked={!!edit.is_active} onChange={e => setEdit({ ...edit, is_active: e.target.checked })} /> <span className="text-slate-200">Active</span></label>
          </div>
          <div className="flex gap-2 mt-3">
            <button data-testid="sp-save" onClick={save} className="px-3 py-2 rounded-lg bg-emerald-500 text-black text-xs font-bold inline-flex items-center gap-1.5"><Save className="w-3.5 h-3.5" /> Save</button>
            <button onClick={() => setEdit(null)} className="px-3 py-2 rounded-lg bg-slate-700 text-slate-200 text-xs">Cancel</button>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-white/10 bg-slate-950/60 p-4 overflow-x-auto">
        {links.length === 0 ? <div className="text-center py-8 text-slate-500 text-sm">No links yet — click "Add Link"</div> : (
          <table className="w-full text-xs">
            <thead className="text-slate-400 text-left"><tr><th className="py-2 pr-3">Order</th><th className="py-2 pr-3">Image</th><th className="py-2 pr-3">Title</th><th className="py-2 pr-3">URL</th><th className="py-2 pr-3">Clicks</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-3">Actions</th></tr></thead>
            <tbody className="text-slate-200">
              {links.map(l => (
                <tr key={l.id} className="border-t border-slate-800">
                  <td className="py-2 pr-3">{l.sort_order}</td>
                  <td className="py-2 pr-3">{l.image_url ? <img src={l.image_url} alt="" className="w-10 h-10 rounded object-cover" /> : '—'}</td>
                  <td className="py-2 pr-3"><div className="font-semibold">{l.title}</div><div className="text-slate-500 text-[10px]">{l.category}</div></td>
                  <td className="py-2 pr-3 max-w-[180px] truncate"><a href={l.url} target="_blank" rel="noreferrer" className="text-amber-300 underline">{l.url}</a></td>
                  <td className="py-2 pr-3 font-bold text-amber-300">{l.clicks || 0}</td>
                  <td className="py-2 pr-3"><span className={`px-2 py-0.5 rounded-full text-[10px] ${l.is_active ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-500/20 text-slate-400'}`}>{l.is_active ? 'ACTIVE' : 'PAUSED'}</span></td>
                  <td className="py-2 pr-3 flex gap-1">
                    <button data-testid={`sp-edit-${l.id}`} onClick={() => setEdit(l)} className="px-2 py-1 rounded bg-slate-700 text-amber-200 inline-flex items-center gap-1 text-[10px]"><Edit3 className="w-3 h-3" /></button>
                    <button data-testid={`sp-del-${l.id}`} onClick={() => del(l.id)} className="px-2 py-1 rounded bg-rose-500 text-white inline-flex items-center gap-1 text-[10px]"><Trash2 className="w-3 h-3" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ============ Live Trading Phase 3 ============
const Phase3Tab = () => {
  const [s, setS] = useState(null);
  const load = () => api.get('/admin/algotrading/settings').then((r) => setS(r.data)).catch(() => toast.error('Admin only'));
  useEffect(() => { load(); }, []);
  const goLive = async () => {
    if (!window.confirm('Activate LIVE TRADING (Phase 3) for ALL users? This enables real-broker order execution platform-wide.')) return;
    try {
      await api.post('/admin/algotrading/phase3/go-live');
      toast.success('🚀 Live Trading Phase 3 ACTIVE');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const halt = async () => {
    try {
      await api.post('/admin/algotrading/kill-switch', { enabled: true, reason: 'Admin halt from console' });
      toast.success('Kill-switch ENABLED — all bots halted');
      load();
    } catch (e) { toast.error('Failed'); }
  };
  if (!s) return <Loading />;
  const live = !!s.live_trading_enabled;
  const kill = !!s.kill_switch;
  return (
    <div className="rounded-2xl border border-white/10 bg-slate-950/60 p-5 space-y-4" data-testid="phase3-panel">
      <div className="flex items-center gap-3">
        <div className={`px-3 py-1.5 rounded-full text-xs font-bold border ${live ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-200' : 'bg-amber-500/20 border-amber-500/40 text-amber-200'}`}>
          {live ? '● LIVE ENABLED' : '○ LIVE DISABLED'}
        </div>
        <div className={`px-3 py-1.5 rounded-full text-xs font-bold border ${kill ? 'bg-rose-500/20 border-rose-500/40 text-rose-200' : 'bg-slate-500/20 border-slate-500/40 text-slate-200'}`}>
          Kill-Switch: {kill ? 'ON' : 'OFF'}
        </div>
        <div className="text-xs text-slate-400">Phase: {s.phase || 'N/A'}</div>
      </div>
      <div className="flex gap-2">
        <button onClick={goLive} disabled={live && !kill} data-testid="phase3-go-live" className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-sm disabled:opacity-50 flex items-center gap-2">
          <Rocket className="w-4 h-4" /> Activate Phase 3 Go-Live
        </button>
        <button onClick={halt} data-testid="phase3-halt" className="px-5 py-2.5 rounded-xl bg-rose-500/15 border border-rose-500/40 text-rose-200 font-bold text-sm flex items-center gap-2">
          <ShieldAlert className="w-4 h-4" /> Emergency Halt
        </button>
      </div>
      <div className="text-[11px] text-slate-400 leading-relaxed">
        Activating Phase 3 lifts kill-switch, enables module, and turns ON real-broker order execution platform-wide. Halt instantly disables all bots.
      </div>
    </div>
  );
};

// ============ Smart Card Limits ============
const SmartCardTab = () => {
  const [s, setS] = useState({ free_limit: 1, active_limit: 5 });
  const [userId, setUserId] = useState('');
  const [limit, setLimit] = useState('');
  useEffect(() => { api.get('/smart-card/admin/settings').then((r) => setS(r.data)).catch(() => toast.error('Admin only')); }, []);
  const save = async () => {
    try { await api.post('/smart-card/admin/settings', s); toast.success('Saved'); }
    catch (e) { toast.error('Failed'); }
  };
  const setUserLimit = async () => {
    if (!userId) { toast.error('user_id required'); return; }
    try {
      await api.post('/smart-card/admin/user-limit', { user_id: userId, limit: limit === '' ? null : Number(limit) });
      toast.success(limit === '' ? 'Override cleared' : `Limit set to ${limit}`);
      setUserId(''); setLimit('');
    } catch (e) { toast.error('Failed'); }
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-slate-950/60 p-5 space-y-4" data-testid="smartcard-panel">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Free Member Limit"><input type="number" min="0" value={s.free_limit} onChange={(e) => setS({...s, free_limit: Number(e.target.value)})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2" data-testid="sc-free-limit" /></Field>
        <Field label="Active Member Limit"><input type="number" min="0" value={s.active_limit} onChange={(e) => setS({...s, active_limit: Number(e.target.value)})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2" data-testid="sc-active-limit" /></Field>
      </div>
      <button onClick={save} data-testid="sc-save" className="px-5 py-2 rounded-xl bg-emerald-500 text-white font-bold text-sm flex items-center gap-2"><Save className="w-4 h-4" /> Save Limits</button>
      <hr className="border-white/10" />
      <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Per-User Override</div>
      <div className="flex gap-2 flex-wrap">
        <input placeholder="user_id" value={userId} onChange={(e) => setUserId(e.target.value)} className="flex-1 min-w-[200px] bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="sc-user-id" />
        <input placeholder="limit (blank = clear)" value={limit} onChange={(e) => setLimit(e.target.value)} className="w-40 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="sc-user-limit" />
        <button onClick={setUserLimit} data-testid="sc-user-save" className="px-4 py-2 rounded-lg bg-amber-500 text-white font-bold text-sm">Apply</button>
      </div>
    </div>
  );
};

// ============ Academy Custom Categories ============
const CategoriesTab = () => {
  const [cats, setCats] = useState([]);
  const [newLabel, setNewLabel] = useState({ en: '', hi: '', mr: '' });
  const load = () => api.get('/academy/categories').then((r) => setCats(r.data.categories));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!newLabel.en) { toast.error('English label required'); return; }
    try {
      await api.post('/academy/admin/categories', { label: newLabel });
      toast.success('Category added');
      setNewLabel({ en: '', hi: '', mr: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this custom category? (Built-in cannot be deleted)')) return;
    try { await api.delete(`/academy/admin/categories/${id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Cannot delete'); }
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-slate-950/60 p-5 space-y-4" data-testid="categories-panel">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
        <Field label="English *"><input value={newLabel.en} onChange={(e) => setNewLabel({...newLabel, en: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="cat-new-en" /></Field>
        <Field label="हिन्दी"><input value={newLabel.hi} onChange={(e) => setNewLabel({...newLabel, hi: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="cat-new-hi" /></Field>
        <Field label="मराठी"><input value={newLabel.mr} onChange={(e) => setNewLabel({...newLabel, mr: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="cat-new-mr" /></Field>
        <button onClick={add} data-testid="cat-add" className="px-4 py-2 rounded-lg bg-emerald-500 text-white font-bold text-sm flex items-center gap-1 justify-center"><Plus className="w-3.5 h-3.5" /> Add</button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
        {cats.map((c) => (
          <div key={c.id} data-testid={`cat-row-${c.id}`} className={`rounded-lg border p-2.5 ${c.is_custom ? 'border-amber-500/30 bg-amber-500/5' : 'border-white/10 bg-white/5'}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">{c.is_custom ? 'CUSTOM' : 'BUILT-IN'}</div>
            <div className="font-bold text-sm">{c.label.en}</div>
            <div className="text-xs text-slate-400">{c.label.hi} · {c.label.mr}</div>
            {c.is_custom && <button onClick={() => del(c.id)} data-testid={`cat-del-${c.id}`} className="mt-1.5 text-[10px] text-rose-300 hover:underline flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>}
          </div>
        ))}
      </div>
    </div>
  );
};

// ============ Academy Course CRUD ============
const CoursesTab = () => {
  const [cats, setCats] = useState([]);
  const [courses, setCourses] = useState([]);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [form, setForm] = useState({ category_id: '', title_en: '', title_hi: '', title_mr: '', desc_en: '', thumbnail_url: '' });
  const [editingCourse, setEditingCourse] = useState(null);
  const [modulesFor, setModulesFor] = useState(null);
  const [modForm, setModForm] = useState({ title_en: '', text_en: '', content_type: 'video', video_url: '', image_url: '', external_url: '', link_label_en: 'Open Link', is_demo: false, points_reward: 5 });

  const load = async () => {
    const cR = await api.get('/academy/categories'); setCats(cR.data.categories);
    const r = await api.get('/academy/courses'); setCourses(r.data.courses);
  };
  useEffect(() => { load(); }, []);

  const autoSeedAll = async () => {
    if (!window.confirm('Generate a starter AI course (5 modules each) for EVERY category that has no courses? This may take 1-2 minutes.')) return;
    setSeeding(true);
    setSeedResult(null);
    try {
      const r = await api.post('/academy/admin/auto-seed-all?language=en');
      setSeedResult(r.data);
      toast.success(`✨ Seeded ${r.data.seeded_count} new courses · skipped ${r.data.skipped_count} (already had)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Auto-seed failed'); }
    finally { setSeeding(false); }
  };

  const saveCourse = async () => {
    if (!form.category_id || !form.title_en) { toast.error('Category & English title required'); return; }
    try {
      const payload = {
        category_id: form.category_id,
        title_i18n: { en: form.title_en, hi: form.title_hi, mr: form.title_mr },
        desc_i18n: { en: form.desc_en },
        thumbnail_url: form.thumbnail_url,
        is_published: true,
      };
      if (editingCourse) {
        await api.put(`/academy/admin/courses/${editingCourse}`, payload);
        toast.success('Updated');
      } else {
        await api.post('/academy/admin/courses', payload);
        toast.success('Course created');
      }
      setForm({ category_id: '', title_en: '', title_hi: '', title_mr: '', desc_en: '', thumbnail_url: '' });
      setEditingCourse(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const delCourse = async (id) => {
    if (!window.confirm('Delete this course AND its modules?')) return;
    try { await api.delete(`/academy/admin/courses/${id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Failed'); }
  };
  const saveModule = async () => {
    if (!modulesFor || !modForm.title_en) { toast.error('Title required'); return; }
    try {
      await api.post('/academy/admin/modules', {
        course_id: modulesFor.id,
        title_i18n: { en: modForm.title_en },
        text_content_i18n: { en: modForm.text_en },
        content_type: modForm.content_type,
        video_url: modForm.content_type === 'video' ? modForm.video_url : null,
        image_url: modForm.content_type === 'image' ? modForm.image_url : null,
        external_url: (modForm.content_type === 'url' || modForm.content_type === 'web_link') ? modForm.external_url : null,
        link_label_i18n: { en: modForm.link_label_en || 'Open Link' },
        is_demo: modForm.is_demo,
        points_reward: Number(modForm.points_reward),
        order_index: 0,
      });
      toast.success('Module added');
      setModForm({ title_en: '', text_en: '', content_type: 'video', video_url: '', image_url: '', external_url: '', link_label_en: 'Open Link', is_demo: false, points_reward: 5 });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="courses-panel">
      {/* AUTO-SEED ALL */}
      <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="auto-seed-panel">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-amber-300 flex items-center gap-1.5"><Rocket className="w-4 h-4" /> AI Bulk Seeder</div>
          <div className="text-sm font-bold mt-0.5">One-click: populate every empty category with a 5-module starter course</div>
          <div className="text-[11px] text-slate-400 mt-1">Uses Emergent LLM (Claude Sonnet 4). Safe to re-run — skips categories that already have courses.</div>
        </div>
        <button onClick={autoSeedAll} disabled={seeding} data-testid="auto-seed-all-btn" className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold text-sm disabled:opacity-60 flex items-center gap-2 shadow-lg">
          <Sparkles className="w-4 h-4" /> {seeding ? 'Seeding (1–2 min)…' : '🤖 Auto-Seed All Categories'}
        </button>
      </div>
      {seedResult && (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs space-y-1" data-testid="seed-result">
          <div className="font-bold text-emerald-200">✅ Seeded: {seedResult.seeded_count} · Skipped: {seedResult.skipped_count} · Failed: {seedResult.failed_count}</div>
          {seedResult.failed && seedResult.failed.length > 0 && <div className="text-rose-300">Failures: {seedResult.failed.map(f => f.category_id).join(', ')}</div>}
        </div>
      )}

      <div className="rounded-2xl border border-white/10 bg-slate-950/60 p-5">
        <div className="text-sm font-bold mb-3">{editingCourse ? 'Edit Course' : 'New Course'}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Field label="Category *"><select value={form.category_id} onChange={(e) => setForm({...form, category_id: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="course-category"><option value="">— select —</option>{cats.map((c) => <option key={c.id} value={c.id}>{c.label.en}</option>)}</select></Field>
          <Field label="Thumbnail URL"><input value={form.thumbnail_url} onChange={(e) => setForm({...form, thumbnail_url: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" /></Field>
          <Field label="Title (English) *"><input value={form.title_en} onChange={(e) => setForm({...form, title_en: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="course-title-en" /></Field>
          <Field label="Title (हिन्दी)"><input value={form.title_hi} onChange={(e) => setForm({...form, title_hi: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" /></Field>
          <Field label="Title (मराठी)"><input value={form.title_mr} onChange={(e) => setForm({...form, title_mr: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" /></Field>
          <Field label="Description (en)"><input value={form.desc_en} onChange={(e) => setForm({...form, desc_en: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" /></Field>
        </div>
        <button onClick={saveCourse} data-testid="course-save" className="mt-3 px-4 py-2 rounded-lg bg-emerald-500 text-white font-bold text-sm flex items-center gap-1.5"><Save className="w-3.5 h-3.5" /> {editingCourse ? 'Update' : 'Create'}</button>
        {editingCourse && <button onClick={() => { setEditingCourse(null); setForm({ category_id: '', title_en: '', title_hi: '', title_mr: '', desc_en: '', thumbnail_url: '' }); }} className="ml-2 text-xs text-slate-400 hover:text-white">Cancel</button>}
      </div>

      <div className="rounded-2xl border border-white/10 bg-slate-950/60 p-5">
        <div className="text-sm font-bold mb-3">Existing Courses ({courses.length})</div>
        <div className="space-y-2">
          {courses.map((c) => (
            <div key={c.id} data-testid={`course-row-${c.id}`} className="rounded-lg border border-white/10 bg-white/5 p-3 flex items-center justify-between gap-2 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="text-[10px] uppercase tracking-wider text-amber-300">{cats.find((x) => x.id === c.category_id)?.label?.en || c.category_id}</div>
                <div className="font-bold text-sm">{c.title || (c.title_i18n || {}).en}</div>
              </div>
              <div className="flex gap-1.5">
                <button onClick={() => setModulesFor(c)} data-testid={`course-modules-${c.id}`} className="px-2.5 py-1 rounded bg-amber-500/20 text-amber-200 border border-amber-500/40 text-xs font-bold">+ Module</button>
                <button onClick={() => delCourse(c.id)} data-testid={`course-del-${c.id}`} className="px-2.5 py-1 rounded bg-rose-500/20 text-rose-200 border border-rose-500/40 text-xs font-bold flex items-center gap-1"><Trash2 className="w-3 h-3" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {modulesFor && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setModulesFor(null)} data-testid="module-modal">
          <div className="bg-slate-900 border border-white/10 rounded-2xl max-w-lg w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="font-bold">Add module to: {modulesFor.title_i18n?.en}</div>
              <button onClick={() => setModulesFor(null)} className="text-slate-400"><X className="w-4 h-4" /></button>
            </div>
            <Field label="Title (English) *"><input value={modForm.title_en} onChange={(e) => setModForm({...modForm, title_en: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="mod-title" /></Field>
            <Field label="Text Content"><textarea rows={4} value={modForm.text_en} onChange={(e) => setModForm({...modForm, text_en: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" /></Field>
            <Field label="Content Type *">
              <select value={modForm.content_type} onChange={(e) => setModForm({...modForm, content_type: e.target.value})}
                data-testid="mod-content-type"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm">
                <option value="video">📹 Video (YouTube/Vimeo embed)</option>
                <option value="image">🖼️ Image (single image URL)</option>
                <option value="url">🔗 External URL (opens in same tab)</option>
                <option value="web_link">🌐 Web Link (opens in new tab)</option>
                <option value="text">📝 Text only</option>
              </select>
            </Field>
            {modForm.content_type === 'video' && (
              <Field label="Video URL (YouTube/Vimeo embed)"><input value={modForm.video_url} onChange={(e) => setModForm({...modForm, video_url: e.target.value})} placeholder="https://www.youtube.com/embed/..." className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="mod-video-url" /></Field>
            )}
            {modForm.content_type === 'image' && (
              <Field label="Image URL"><input value={modForm.image_url} onChange={(e) => setModForm({...modForm, image_url: e.target.value})} placeholder="https://.../image.jpg" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="mod-image-url" /></Field>
            )}
            {(modForm.content_type === 'url' || modForm.content_type === 'web_link') && (
              <>
                <Field label="External URL"><input value={modForm.external_url} onChange={(e) => setModForm({...modForm, external_url: e.target.value})} placeholder="https://..." className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="mod-external-url" /></Field>
                <Field label="Button Label (English)"><input value={modForm.link_label_en} onChange={(e) => setModForm({...modForm, link_label_en: e.target.value})} placeholder="Open Link" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" /></Field>
              </>
            )}
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={modForm.is_demo} onChange={(e) => setModForm({...modForm, is_demo: e.target.checked})} /> Free Demo</label>
              <Field label="Reward AT"><input type="number" min="0" max="20" value={modForm.points_reward} onChange={(e) => setModForm({...modForm, points_reward: e.target.value})} className="w-24 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" /></Field>
            </div>
            <button onClick={saveModule} data-testid="mod-save" className="w-full px-4 py-2 rounded-lg bg-emerald-500 text-white font-bold text-sm">Add Module</button>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1 font-semibold">{label}</div>
    {children}
  </label>
);
const Loading = () => <div className="text-slate-400 text-sm">Loading…</div>;

// ============ Feelings Personas Tab ============
const FeelingsPersonasTab = () => {
  const [personas, setPersonas] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [edits, setEdits] = useState({});
  const [savingId, setSavingId] = useState(null);

  const VOICE_OPTIONS = ['alloy', 'ash', 'coral', 'echo', 'fable', 'nova', 'onyx', 'sage', 'shimmer'];

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get('/anant-feelings/admin/personas'),
        api.get('/anant-feelings/admin/sessions', { params: { limit: 25 } }),
      ]);
      setPersonas(a.data.personas || []);
      setSessions(b.data.sessions || []);
      const initial = {};
      (a.data.personas || []).forEach((p) => { initial[p.id] = { ...p }; });
      setEdits(initial);
    } catch (e) { toast.error('Admin only or failed to load'); }
  };
  useEffect(() => { load(); }, []);

  const save = async (id) => {
    const payload = { ...edits[id] };
    delete payload.id; delete payload.created_at; delete payload.updated_at;
    setSavingId(id);
    try {
      await api.post(`/anant-feelings/admin/persona/${id}`, payload);
      toast.success(`${edits[id]?.name || id} updated`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSavingId(null); }
  };

  if (!personas) return <Loading />;

  return (
    <div className="space-y-6" data-testid="feelings-admin-tab">
      <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-4 text-sm text-rose-100">
        <div className="font-bold text-rose-300 mb-1 flex items-center gap-2"><Heart className="w-4 h-4" /> Anant Feelings · Direct Dil Se</div>
        <div className="text-rose-100/80">3 personas · 200 AT / 10 min · OpenAI TTS · 7-level webchain commission split.</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {personas.map((p) => {
          const e = edits[p.id] || p;
          const set = (k, v) => setEdits((s) => ({ ...s, [p.id]: { ...s[p.id], [k]: v } }));
          return (
            <div key={p.id} className="rounded-xl border border-white/10 bg-slate-900/60 p-4 space-y-3" data-testid={`feelings-admin-${p.id}`}>
              <div className="flex items-center justify-between">
                <div className="font-extrabold text-amber-200">{p.name} <span className="text-xs text-slate-400">· {p.gender}</span></div>
                <button
                  onClick={() => set('is_active', !e.is_active)}
                  className={`text-[10px] font-bold px-2 py-1 rounded ${e.is_active ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-700 text-slate-300'}`}
                  data-testid={`feelings-toggle-${p.id}`}
                >{e.is_active ? 'ACTIVE' : 'DISABLED'}</button>
              </div>
              <div className={`h-20 rounded-lg bg-gradient-to-br ${e.gradient || 'from-rose-500 to-purple-600'} flex items-center justify-center text-white text-3xl font-black`}>
                {p.name?.[0]}
              </div>
              <Field label="Tagline">
                <input value={e.tagline || ''} onChange={(ev) => set('tagline', ev.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-sm text-white" />
              </Field>
              <Field label="Video loop URL (mp4/webm — leave blank for animated gradient)">
                <input value={e.video_loop_url || ''} onChange={(ev) => set('video_loop_url', ev.target.value)}
                  placeholder="https://..." data-testid={`feelings-video-${p.id}`}
                  className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-xs text-white font-mono" />
              </Field>
              <Field label="Avatar URL (square image, optional)">
                <input value={e.avatar_url || ''} onChange={(ev) => set('avatar_url', ev.target.value)}
                  placeholder="https://..."
                  className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-xs text-white font-mono" />
              </Field>
              <Field label="Gradient (Tailwind classes)">
                <input value={e.gradient || ''} onChange={(ev) => set('gradient', ev.target.value)}
                  placeholder="from-rose-500 via-pink-500 to-purple-600"
                  className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-xs text-white font-mono" />
              </Field>
              <Field label="TTS Voice (OpenAI)">
                <select value={e.tts_voice || 'nova'} onChange={(ev) => set('tts_voice', ev.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-sm text-white">
                  {VOICE_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </Field>
              <Field label="System Prompt">
                <textarea value={e.system_prompt || ''} onChange={(ev) => set('system_prompt', ev.target.value)}
                  rows={5} className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-xs text-white" />
              </Field>
              <button onClick={() => save(p.id)} disabled={savingId === p.id}
                data-testid={`feelings-save-${p.id}`}
                className="w-full px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2">
                <Save className="w-4 h-4" /> {savingId === p.id ? 'Saving…' : 'Save'}
              </button>
            </div>
          );
        })}
      </div>

      <div>
        <div className="text-amber-300 font-bold text-sm mb-2">Recent Sessions</div>
        <div className="rounded-xl border border-white/10 overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-900 text-slate-400">
              <tr><th className="px-2 py-2 text-left">When</th><th className="px-2 py-2 text-left">User</th><th className="px-2 py-2">Persona</th><th className="px-2 py-2">Cost</th><th className="px-2 py-2">Status</th></tr>
            </thead>
            <tbody>
              {sessions.length === 0 && <tr><td colSpan={5} className="text-center text-slate-500 py-4">No sessions yet</td></tr>}
              {sessions.map((s) => (
                <tr key={s.id} className="border-t border-white/5">
                  <td className="px-2 py-1.5 text-slate-300">{(s.started_at || '').slice(0, 19).replace('T', ' ')}</td>
                  <td className="px-2 py-1.5 text-slate-400 font-mono">{(s.user_id || '').slice(0, 8)}…</td>
                  <td className="px-2 py-1.5 text-center text-amber-200">{s.persona_name}</td>
                  <td className="px-2 py-1.5 text-center text-amber-300">{s.cost_at} AT</td>
                  <td className="px-2 py-1.5 text-center"><span className={`px-2 py-0.5 rounded text-[10px] ${s.status === 'active' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-700 text-slate-300'}`}>{s.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AnantAdminConsole;
